import type { LinkProps } from "@remix-run/react";
import type { ComponentProps, ReactNode } from "react";

import { Link } from "@remix-run/react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  AlertCircleIcon,
  CheckCircleIcon,
  TriangleAlertIcon,
} from "lucide-react";

import type { ClassName } from "~/utils/cn";
import { cn } from "~/utils/cn";
import { isExternalLink } from "~/utils/is-external-link";

const variantIcons = {
  default: null,
  danger: AlertCircleIcon,
  "muted-danger": AlertCircleIcon,
  warning: TriangleAlertIcon,
  success: CheckCircleIcon,
  info: AlertCircleIcon,
} as const;

const alertVariants = cva(
  "grid gap-0.5 rounded-lg border border-transparent px-4 py-3 text-left text-sm has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2.5 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4 w-full group/alert",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        danger:
          "text-destructive bg-destructive-bg *:data-[slot=alert-description]:text-destructive/90 *:[svg]:text-current",
        "muted-danger":
          "text-muted-foreground bg-muted *:data-[slot=alert-description]:text-muted-foreground/90 *:[svg]:text-current",
        warning:
          "text-warning bg-warning-bg *:data-[slot=alert-description]:text-warning/90 *:[svg]:text-current",
        success:
          "text-success bg-success-bg *:data-[slot=alert-description]:text-success/90 *:[svg]:text-current",
        info: "text-info bg-info-bg *:data-[slot=alert-description]:text-info/90 *:[svg]:text-current",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Alert({
  className,
  variant,
  icon,
  children,
  ...props
}: ComponentProps<"div"> &
  VariantProps<typeof alertVariants> & {
    icon?: ReactNode;
  }) {
  const DefaultIcon = variant ? variantIcons[variant] : null;
  const renderedIcon =
    icon === undefined ? DefaultIcon ? <DefaultIcon /> : null : icon;

  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {renderedIcon}
      {children}
    </div>
  );
}

function AlertTitle({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "font-medium group-has-[>svg]/alert:col-start-2 [&_a]:hover:text-foreground [&_a]:underline [&_a]:underline-offset-3",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-muted-foreground text-sm text-balance md:text-pretty [&_p:not(:last-child)]:mb-4 [&_a]:hover:text-foreground [&_a]:underline [&_a]:underline-offset-3",
        className,
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("w-fit mt-1 col-span-full", className)}
      {...props}
    />
  );
}

function AlertLink({
  href,
  label,
  icon,
  target,
  reloadDocument,
  replace,
  className,
  rel,
}: AlertLinkProps) {
  const content = (
    <>
      {label}{" "}
      {icon === undefined ? <span aria-hidden="true">&rarr;</span> : icon}
    </>
  );

  if (isExternalLink(href)) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={cn(
          "w-fit mt-1 col-span-full whitespace-nowrap underline",
          className,
        )}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      rel={rel}
      to={href}
      target={target}
      replace={replace}
      reloadDocument={reloadDocument}
      className={cn(
        "w-fit mt-1 col-span-full whitespace-nowrap underline",
        className,
      )}
    >
      {content}
    </Link>
  );
}

function AlertCode({ className, ...props }: ComponentProps<"code">) {
  return (
    <code
      data-slot="alert-code"
      className={cn("text-sm font-normal select-all", className)}
      {...props}
    />
  );
}

function AlertBody({ body }: { body: AlertMessage }) {
  if (Array.isArray(body) && body.length > 1) {
    return (
      <ul className="list-disc pl-5 v-stack gap-y-1">
        {body.map((item, index) => (
          // oxlint-disable-next-line no-array-index-key
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  }
  return <p>{Array.isArray(body) ? body[0] : body}</p>;
}

type AlertLinkProps = {
  href: HTMLAnchorElement["href"];
  label: ReactNode;
  icon?: ReactNode;
  target?: HTMLAnchorElement["target"];
  reloadDocument?: LinkProps["reloadDocument"];
  replace?: LinkProps["replace"];
  rel?: HTMLAnchorElement["rel"];
  className?: ClassName;
};

type AlertType = "danger" | "muted-danger" | "warning" | "success" | "info";

type AlertMessage = ReactNode | ReactNode[];

type StaticAlertBaseProps = {
  type: AlertType;
  link?: Omit<AlertLinkProps, "label" | "icon"> & { label: string };
  code?: string;
};

type StaticAlertProps = StaticAlertBaseProps &
  (
    | { title: string; body?: string | string[] }
    | { title?: string; body: string | string[] }
  );

function StaticAlert({ type, title, body, link, code }: StaticAlertProps) {
  return (
    <Alert variant={type}>
      {title && <AlertTitle>{title}</AlertTitle>}
      {body && (
        <AlertDescription>
          <AlertBody body={body} />
        </AlertDescription>
      )}
      {link && <AlertLink {...link} />}
      {code && <AlertCode>{code}</AlertCode>}
    </Alert>
  );
}

export {
  Alert,
  AlertTitle,
  AlertDescription,
  AlertAction,
  AlertLink,
  AlertCode,
  AlertBody,
  StaticAlert,
};

export type { StaticAlertProps, AlertLinkProps, AlertType, AlertMessage };
